import { Skeleton, cx } from "@unizpay/ui";
import type { ReactNode } from "react";
import { Link } from "react-router";

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  align?: "left" | "right";
  /** Extra classes for this column's cells, e.g. `whitespace-nowrap`. */
  className?: string;
}

const cellPad = "px-3 first:pl-4 last:pr-4 sm:first:pl-5 sm:last:pr-5";

/**
 * Dense table. Scrolls horizontally below `minWidth` so it never overflows the page.
 * With `rowHref` the whole row is clickable: the first cell holds a real link whose
 * overlay covers the row, so keyboard and screen reader users get a normal link.
 */
export function DataTable<T>({
  label,
  columns,
  rows,
  rowKey,
  rowHref,
  rowLabel,
  loading = false,
  skeletonRows = 5,
  empty,
  dimmed = false,
  minWidth = "40rem",
}: {
  label: string;
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  rowHref?: (row: T) => string;
  /** Accessible name of the row link; defaults to the first cell's text. */
  rowLabel?: (row: T) => string;
  loading?: boolean;
  skeletonRows?: number;
  empty?: ReactNode;
  /** Fade rows while a new page loads over the old one. */
  dimmed?: boolean;
  minWidth?: string;
}) {
  if (!loading && rows && rows.length === 0 && empty) return <>{empty}</>;

  return (
    <div className="overflow-x-auto">
      <table
        className="w-full text-left text-[0.8125rem]"
        style={{ minWidth }}
        aria-busy={loading || undefined}
      >
        <caption className="sr-only">{label}</caption>
        <thead>
          <tr className="border-b border-line">
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={cx(
                  cellPad,
                  "py-2 text-xs font-medium whitespace-nowrap text-fg-muted",
                  c.align === "right" && "text-right",
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={cx(dimmed && "opacity-60 transition-opacity")}>
          {loading || !rows
            ? Array.from({ length: skeletonRows }, (_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder rows
                <tr key={i} className="border-b border-line last:border-0">
                  {columns.map((c) => (
                    <td key={c.key} className={cx(cellPad, "py-2.5")}>
                      <Skeleton className={cx("h-3.5", c.align === "right" ? "ml-auto w-16" : "w-24")} />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row) => {
                const href = rowHref?.(row);
                return (
                  <tr
                    key={rowKey(row)}
                    className={cx(
                      "border-b border-line last:border-0",
                      href && "relative cursor-pointer hover:bg-subtle focus-within:bg-subtle",
                    )}
                  >
                    {columns.map((c, i) => (
                      <td
                        key={c.key}
                        className={cx(
                          cellPad,
                          "py-2 align-middle",
                          c.align === "right" && "text-right",
                          c.className,
                        )}
                      >
                        {href && i === 0 ? (
                          <Link
                            to={href}
                            aria-label={rowLabel?.(row)}
                            className="rounded outline-none after:absolute after:inset-0 after:content-[''] focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-ring"
                          >
                            {c.cell(row)}
                          </Link>
                        ) : (
                          c.cell(row)
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
        </tbody>
      </table>
    </div>
  );
}
